import { useAuth0 } from "@auth0/auth0-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Form, Alert, Button } from "react-bootstrap";
import type { UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import { useQueryHousehold } from "../clients/household/lib";
import Content from "../components/Content";
import SubmitButton from "../components/SubmitButton";
import {
  buildManualRebalance,
  type ManualAccountResults,
} from "./buildManualRebalance";
import RebalanceHouseholdExplainV3 from "./explain/RebalanceHouseholdExplain";
import type { ExplainResponseV3 } from "./explain/lib/explainTypes";
import { RebalanceContext } from "./RebalanceInfo";

// The inline explain_v3 payload is PII-free by design (account name/number are
// excluded -- see `useAccountResolver`). The persisted explain page joins that
// identity back in client-side from the household it already loaded; the manual
// upload now does the same. `request.household_id` is threaded through from the
// uploaded xlsx's Household sheet (APP-1248, python `app.py`), so we fetch the
// household by id here and supply it via `RebalanceContext`. The explain tree's
// `useAccountResolver` / `useHouseholdResolver` then resolve account name,
// masked number, model name, and the header household name -- identical to the
// persisted page. When no Household ID was uploaded (`household_id === 0`) the
// fetch stays disabled and the resolvers fall back to `Account <id>` /
// `Household <id>`, preserving the prior behaviour.
//
// We also synthesise a charts-only `rebalance` from the response and add it to
// the context so the per-account `RebalanceAccountInsights` widget renders --
// the persisted page gets this from `useQueryRebalance`, which the manual flow
// has no equivalent for (no persisted `rebalance_s` row). See
// `buildManualRebalance` for what each metric maps to and which are
// intentionally omitted (risk-allocation bars + Post-Rebalance asset series
// have no explain_v3 source). The widget's own by-id fetches still light up
// the account header, model, and Current/Target asset-allocation chart.
const ManualRebalanceExplainV3 = ({
  response,
  perAccountResults,
}: {
  response: ExplainResponseV3;
  perAccountResults?: ManualAccountResults;
}) => {
  const householdId = response.request.household_id;
  const { data: household } = useQueryHousehold(householdId, {
    includeAccounts: true,
    enabled: householdId > 0,
  });
  const rebalance = useMemo(
    () => buildManualRebalance(response, perAccountResults),
    [response, perAccountResults],
  );
  return (
    <RebalanceContext.Provider value={{ household, rebalance }}>
      <RebalanceHouseholdExplainV3 response={response} />
    </RebalanceContext.Provider>
  );
};

const ManualRebalanceGenerator = () => {
  const uploadRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<object>();
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { getAccessTokenSilently } = useAuth0();

  const onSubmit = useCallback(
    async (ev: React.FormEvent) => {
      ev.preventDefault();
      if (!selectedFile) return;

      setIsSubmitting(true);
      setResult(undefined);

      try {
        const accessToken = await getAccessTokenSilently();
        const formData = new FormData();
        formData.append("file", selectedFile);

        const response = await fetch(
          `${process.env.REACT_APP_API_ROOT}/rebalances/manual/xlsx`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "An error occurred");
        }

        const data = (await response.json()) as UnpackResponse<
          RebalancesController["manualRebalanceGeneration"]
        >;
        setResult(data.data);
      } catch (err) {
        setResult({
          error: err instanceof Error ? err.message : "An error occurred",
        });
      } finally {
        setIsSubmitting(false);

        if (uploadRef.current !== null) {
          uploadRef.current.value = "";
        }
        setSelectedFile(null);
      }
    },
    [selectedFile, getAccessTokenSilently],
  );

  const onSelectFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files && ev.target.files.length > 0) {
      setSelectedFile(ev.target.files[0]);
    }
  };

  const copyToClipboard = useCallback(async () => {
    if (!result) return;
    await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [result]);

  const explainV3 =
    result && !("error" in result)
      ? (result as { explain_v3: ExplainResponseV3 | null }).explain_v3
      : null;

  return (
    <Content>
      <h1>Manual Rebalance</h1>
      <Form onSubmit={onSubmit}>
        <Form.Group controlId="form-file" className="mb-3">
          <Form.Label>Rebalance File (XLSX)</Form.Label>
          <Form.Control
            type="file"
            ref={uploadRef}
            onChange={onSelectFile}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            required
          />
          <Form.Text className="text-muted">
            Upload an Excel file (.xlsx) containing household and account data.
            Templates:{" "}
            <a
              href="/templates/manual_rebalance_nontaxable_template.xlsx"
              download
            >
              Non-Taxable
            </a>
            {" | "}
            <a
              href="/templates/manual_rebalance_taxable_template.xlsx"
              download
            >
              Taxable
            </a>
          </Form.Text>
        </Form.Group>
        <SubmitButton
          className="mb-3"
          isSubmitting={isSubmitting}
          label="Start"
        />
      </Form>
      <h2>Result</h2>
      {typeof result === "undefined" ? (
        <em>
          No result yet. Upload an XLSX file to generate a manual rebalance.
        </em>
      ) : "error" in result ? (
        <Alert variant="danger">
          <strong>Error:</strong> {(result as { error: string }).error}
        </Alert>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span>Manual Rebalance Results</span>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={copyToClipboard}
            >
              {copied ? "Copied!" : "Copy JSON to clipboard"}
            </Button>
          </div>

          {explainV3 ? (
            <ManualRebalanceExplainV3
              response={explainV3}
              perAccountResults={result as ManualAccountResults}
            />
          ) : (
            <Alert variant="info">
              This rebalance generated no explain data to display.
            </Alert>
          )}
        </>
      )}
    </Content>
  );
};

export default ManualRebalanceGenerator;
